/**
 * BUILD-PART BUILDERS — the `BuildPart` vocabulary as constructors.
 *
 * `voxel/` because a part is a voxel-system object (buildTypes.BuildPart) and these know nothing about
 * terrain: `sdfFromConfig` gives each shape its meaning, and `drawing.rasterizePartsAtScale` decides
 * what lattice it lands on. What LOD policy a caller wants from them — how fat a coarse trunk must be
 * to survive its stride, how far a stand-in sinks into the ground — lives with the caller, in
 * terrain/stamps/coarseModel.ts.
 *
 * SIZE SEMANTICS COME FROM sdfFromConfig, and they are per-shape: a sphere reads size.x as its radius,
 * a cylinder reads size.x as radius and size.y as HALF-height, a cone reads size.z as its TOP radius.
 * Sizes here are in whatever voxels the caller is authoring in (FINE voxels for a stamp at any level —
 * the rasterizer divides by the stride, so one part list stays valid across every level it serves).
 *
 * TWO RULES GOVERN EVERY BUILDER HERE, both consequences of `bestWeight > 0` in the rasterizer — a
 * voxel is emitted only where the SDF is STRICTLY inside:
 *
 *  1. PARTS MUST OVERLAP, NEVER MERELY TOUCH. Where two parts share a face the SDF is exactly zero on
 *     both, so NEITHER emits and the row comes out empty. Models arrange this themselves: a tree's
 *     trunk runs PAST the crown base, a building's wall runs one stride past its eave.
 *  2. NO SUB-VOXEL PARTS, unless a one-voxel column is what you mean. A cylinder thinner than the
 *     lattice still contains the lattice point on its own axis, so it emits a ONE-VOXEL-WIDE COLUMN.
 *     That is a spike above a pine's leaves and it is exactly right for a distant trunk; `pinPart`
 *     exists to say the second one deliberately.
 */
import { BuildMode, BuildShape } from './buildTypes.js';
/**
 * Radius (in COARSE voxels) for a part meant to render as a ONE-VOXEL-WIDE, fully solid column.
 *
 * Just under 1 for two reasons at once: at a depth of one coarse voxel the weight packs to solid
 * (that is the terrain ramp), and the neighbouring lattice point at |x| = 1 coarse voxel falls on or
 * outside the surface and is dropped. Staying a hair under 1 keeps that off the float boundary.
 *
 * A thin column is the RIGHT shape twice over: a trunk at ring distance is a thin vertical column,
 * and so is a whole tree once the tree is narrower than a voxel.
 */
export const PIN_COARSE_RADIUS = 0.99;
/** Vertical cylinder standing on the ground, `height` fine voxels tall. */
export function cylinderPart(radius, height, material, baseY = 0) {
    return {
        config: {
            shape: BuildShape.CYLINDER,
            mode: BuildMode.ADD,
            size: { x: radius, y: height / 2, z: radius },
            material,
        },
        offset: { x: 0, y: baseY + height / 2, z: 0 },
    };
}
/** Sphere centred `centreY` fine voxels above the stamp origin. */
export function spherePart(radius, centreY, material) {
    return {
        config: {
            shape: BuildShape.SPHERE,
            mode: BuildMode.ADD,
            size: { x: radius, y: radius, z: radius },
            material,
        },
        offset: { x: 0, y: centreY, z: 0 },
    };
}
/**
 * An OBLATE DOME centred `centreY` fine voxels up — a sphere with its own vertical radius.
 *
 * The big oak's crown, and the reason `sdfFromConfig` learned to read `size.y` on a SPHERE at all.
 * That crown is 16 m across and 12 m tall: a sphere wide enough to match its footprint stands 2 m
 * proud of the tree, and a CONE — what this tree's coarse levels used to draw — is a Christmas tree
 * from any distance you can still tell one species from another. LOD 0 hangs its leaf clusters on
 * exactly this ellipsoid (`envelopeRadius`), so the two agree by construction rather than by anyone
 * remembering to edit both.
 */
export function ellipsoidPart(radiusXZ, radiusY, centreY, material) {
    return {
        config: {
            shape: BuildShape.SPHERE,
            mode: BuildMode.ADD,
            size: { x: radiusXZ, y: radiusY, z: radiusXZ },
            material,
        },
        offset: { x: 0, y: centreY, z: 0 },
    };
}
/**
 * A one-voxel-wide column over an arbitrary span — the pin's top rows in a second material. A tower
 * at lod 4 is 1.6 voxels across and 4 rows tall: it is a pin, but a pin whose top row is roof
 * coloured still reads as a tower with a roof.
 */
export function pinSpanPart(y0, y1, material, stride) {
    return cylinderPart(PIN_COARSE_RADIUS * stride, y1 - y0, material, y0);
}
/**
 * A smooth capped cone spanning [y0, y1] — one part, so no step plateaus and no junction to leave an
 * empty row. This replaced a stack of tapering cylinders, which stepped in visible shoulders
 * (25 -> 21 -> 9 voxels per row on a lod-1 pine) because a stack can only change radius in jumps, and
 * could not be refined: every extra step needs an overlap with the next, so finer steps just fatten it.
 *
 * `topRadius` is the shape's OWN tip, not a function of the stride: 0 loses the whole upper crown its
 * body (a linear cone is half its base radius at mid-height) and draws a needle, while pinning it to
 * one coarse voxel gives a flat plateau. Pass what the real model tapers to — for a pine, 1 fine
 * voxel — or pass baseRadius for a drum where there are too few coarse rows to resolve a taper at all.
 */
export function conePart(baseRadius, topRadius, y0, y1, material) {
    return {
        config: {
            shape: BuildShape.CONE,
            mode: BuildMode.ADD,
            size: { x: baseRadius, y: (y1 - y0) / 2, z: topRadius },
            material,
        },
        offset: { x: 0, y: (y0 + y1) / 2, z: 0 },
    };
}
/** Axis-aligned box, `halfX`/`halfZ` half-extents in fine voxels, spanning [y0, y1]. */
export function boxPart(halfX, halfZ, y0, y1, material) {
    return {
        config: {
            shape: BuildShape.CUBE,
            mode: BuildMode.ADD,
            size: { x: halfX, y: (y1 - y0) / 2, z: halfZ },
            material,
        },
        offset: { x: 0, y: (y0 + y1) / 2, z: 0 },
    };
}
/**
 * A gable roof spanning [y0, y1]: two right-triangle prisms, the second at yaw π, ridge along Z.
 *
 * `sdfPrism` is a RIGHT triangle — full width at its base, one vertical leg at its −X edge — so a
 * symmetric gable is that prism plus its mirror, and a mirror about Y IS a yaw of π (see
 * BuildPart.yaw). The two halves CROSS the ridge plane by `overlap` rather than meeting on it: parts
 * that merely touch both read exactly zero there and NEITHER emits, which would leave the ridge
 * column empty (the pine trunk/canopy gap, in the vertical). The crossing also gives the ridge a
 * flat top `2 * overlap` wide, which is what the LOD-0 roof has anyway — its slope truncates at a
 * 2.8-5.2 fine-voxel-wide ridge rather than closing to a point.
 */
export function gableParts(halfWidth, halfDepth, y0, y1, material, stride) {
    const overlap = stride;
    const size = { x: (halfWidth + overlap) / 2, y: (y1 - y0) / 2, z: halfDepth };
    const offX = (halfWidth - overlap) / 2;
    const offY = (y0 + y1) / 2;
    const config = { shape: BuildShape.PRISM, mode: BuildMode.ADD, size, material };
    return [
        { config, offset: { x: offX, y: offY, z: 0 } },
        { config, offset: { x: -offX, y: offY, z: 0 }, yaw: Math.PI },
    ];
}
//# sourceMappingURL=buildParts.js.map